import { createContext, useCallback, useContext, useMemo, useState } from 'react';

// Deliberately NOT under LOCAL_PREFERENCE_PREFIX ('bh.'). Keys with that prefix
// survive logout, and "the panel was open" is per-user state, not a device
// preference — carrying it across a logout would open one user's assistant for
// the next account signing in on the same machine.
const OPEN_STORAGE_KEY = 'assistantPanelOpen';

const readStoredOpen = () => {
  try {
    return localStorage.getItem(OPEN_STORAGE_KEY) === '1';
  } catch (e) {
    // Storage throws in some private-browsing modes.
    return false;
  }
};

const writeStoredOpen = (open) => {
  try {
    localStorage.setItem(OPEN_STORAGE_KEY, open ? '1' : '0');
  } catch (e) {
    // Not being able to remember the panel state must not break opening it.
  }
};

const AssistantContext = createContext({
  open: false,
  openAssistant: () => {},
  closeAssistant: () => {},
  toggleAssistant: () => {}
});

export const useAssistant = () => useContext(AssistantContext);

/**
 * Holds nothing but "is the assistant panel open".
 *
 * The trigger lives in the top nav (inside the route tree, remounted on every
 * navigation) and the panel lives in App, above the router — so the two cannot
 * share component state. The panel's iframe is mounted once and never
 * unmounted; this flag only decides whether it is on screen.
 */
export function AssistantProvider({ children }) {
  const [open, setOpen] = useState(readStoredOpen);

  const set = useCallback((next) => {
    setOpen(next);
    writeStoredOpen(next);
  }, []);

  const value = useMemo(
    () => ({
      open,
      openAssistant: () => set(true),
      closeAssistant: () => set(false),
      toggleAssistant: () => set(!open)
    }),
    [open, set]
  );

  return <AssistantContext.Provider value={value}>{children}</AssistantContext.Provider>;
}

export { OPEN_STORAGE_KEY };
